import { useEffect, useState } from "react";
import { Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DateField, Field, inputCls } from "@/components/common/Field";
import PicSelect from "@/components/common/PicSelect";
import ProductPicker from "@/components/common/ProductPicker";
import LinesEditor, { cleanLines } from "@/components/common/LinesEditor";
import { PrimaryBtn } from "@/components/common/ui";
import { api, errMsg } from "@/lib/api";

const emptyLine = { product_id: "", qty: 1, output: "roti" };
const emptyBakeLine = { product_id: "", output_product_id: "", qty: 1 };

function useFormReset(open, defaultDate, firstLine = emptyLine) {
  const [date, setDate] = useState(defaultDate);
  const [pic, setPic] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([firstLine]);
  useEffect(() => {
    if (open) { setDate(defaultDate); setNotes(""); setLines([firstLine]); }
  }, [open, defaultDate]); // eslint-disable-line react-hooks/exhaustive-deps
  return { date, setDate, pic, setPic, notes, setNotes, lines, setLines };
}

function Shell({ open, onClose, title, desc, testid, children }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function BatchForm({ open, onClose, products, defaultDate, onSaved }) {
  const f = useFormReset(open, defaultDate);
  const [batchNo, setBatchNo] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && f.date) api.get("/production/next-batch", { params: { date: f.date } }).then((r) => setBatchNo(r.data.batch_no)).catch(() => {});
  }, [open, f.date]);
  const submit = async () => {
    const lines = cleanLines(f.lines);
    if (!lines.length) return toast.error("Isi minimal 1 produk");
    setSaving(true);
    try {
      const { data } = await api.post("/production/batch", { date: f.date, batch_no: Number(batchNo) || null, pic_user_id: f.pic, notes: f.notes, lines });
      toast.success(`Batch ${data.batch_no} tersimpan · ${data.txn_no}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Shell open={open} onClose={onClose} title="Batch Baru" desc="Masukkan hasil satu batch oven. Pilih output Roti Jadi atau Frozen Dough per baris." testid="batch-form">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <DateField value={f.date} onChange={f.setDate} testid="batch-date" />
          <Field label="No. Batch" hint="Otomatis per hari, bisa diubah"><input type="number" value={batchNo} onChange={(e) => setBatchNo(e.target.value)} className={inputCls} data-testid="batch-no" /></Field>
        </div>
        <PicSelect value={f.pic} onChange={f.setPic} testid="batch-pic" />
        <Field label="Produk dalam batch" required>
          <LinesEditor lines={f.lines} setLines={f.setLines} products={products} withOutput testid="batch-lines"
            filter={(p) => ["Roti", "Bagel"].includes(p.category) && !p.is_bundle} />
        </Field>
        <Field label="Catatan"><Textarea value={f.notes} onChange={(e) => f.setNotes(e.target.value)} data-testid="batch-notes" /></Field>
        <PrimaryBtn className="w-full" onClick={submit} disabled={saving} data-testid="batch-submit">{saving ? "Menyimpan…" : "Simpan Batch ke Dapur"}</PrimaryBtn>
      </div>
    </Shell>
  );
}

function bakeHint(l, products) {
  const fd = products.find((p) => p.id === l.product_id);
  const out = products.find((p) => p.id === l.output_product_id);
  const q = Number(l.qty || 0);
  if (!fd || !out || q <= 0) return "Pilih FD asal & produk jadi";
  return `−${q} ${fd.name} → +${q} ${out.name}`;
}

function BakeLines({ lines, setLines, products }) {
  const isFd = (p) => p.category === "Frozen Dough";
  const isFinished = (p) => ["Roti", "Bagel"].includes(p.category) && !p.is_bundle;
  const update = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i) => setLines(lines.filter((_, j) => j !== i));
  const add = () => setLines([...lines, { ...emptyBakeLine }]);
  return (
    <div className="space-y-3" data-testid="bake-lines">
      {lines.map((l, i) => (
        <div key={i} className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3" data-testid={`bake-lines-row-${i}`}>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Frozen Dough asal</div>
              <ProductPicker products={products} value={l.product_id} onChange={(v) => update(i, { product_id: v })} filter={isFd} showStock placeholder="Pilih Frozen Dough" testid={`bake-fd-${i}`} />
            </div>
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Jadi produk</div>
              <ProductPicker products={products} value={l.output_product_id} onChange={(v) => update(i, { output_product_id: v })} filter={isFinished} placeholder="Pilih produk jadi" testid={`bake-out-${i}`} />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex h-11 items-stretch overflow-hidden rounded-md border border-zinc-300 bg-white">
              <button type="button" onClick={() => update(i, { qty: Math.max(1, Number(l.qty || 0) - 1) })} className="press grid w-10 place-items-center hover:bg-zinc-100" data-testid={`bake-qty-${i}-minus`}><Minus className="h-4 w-4" /></button>
              <input type="number" inputMode="numeric" min={1} value={l.qty} onChange={(e) => update(i, { qty: e.target.value === "" ? "" : Number(e.target.value) })} className="num w-16 border-x border-zinc-200 text-center text-base font-bold focus:outline-none" data-testid={`bake-qty-${i}`} />
              <button type="button" onClick={() => update(i, { qty: Number(l.qty || 0) + 1 })} className="press grid w-10 place-items-center hover:bg-zinc-100" data-testid={`bake-qty-${i}-plus`}><Plus className="h-4 w-4" /></button>
            </div>
            <span className="min-w-0 flex-1 truncate text-[11px] text-zinc-500" data-testid={`bake-hint-${i}`}>{bakeHint(l, products)}</span>
            <button type="button" onClick={() => remove(i)} disabled={lines.length === 1} data-testid={`bake-lines-remove-${i}`}
              className="press grid h-11 w-11 shrink-0 place-items-center rounded-md border border-zinc-300 text-zinc-500 hover:border-brand hover:text-brand disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add} data-testid="bake-lines-add"
        className="press w-full rounded-lg border-2 border-dashed border-zinc-300 py-3 text-sm font-semibold text-zinc-600 hover:border-ink hover:text-ink">
        + Tambah baris produk
      </button>
    </div>
  );
}

export function BakeForm({ open, onClose, products, defaultDate, onSaved }) {
  const f = useFormReset(open, defaultDate, { ...emptyBakeLine });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const lines = f.lines
      .filter((l) => l.product_id && l.output_product_id && Number(l.qty) > 0)
      .map(({ product_id, output_product_id, qty }) => ({ product_id, output_product_id, qty: Number(qty) }));
    if (!lines.length) return toast.error("Lengkapi Frozen Dough asal, produk jadi, dan jumlah pada tiap baris");
    setSaving(true);
    try {
      const { data } = await api.post("/production/bake-fd", { date: f.date, pic_user_id: f.pic, notes: f.notes, lines });
      toast.success(`Panggang FD tersimpan · ${data.txn_no}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Shell open={open} onClose={onClose} title="Panggang dari Frozen Dough"
      desc="Pilih Frozen Dough asal & produk jadi tiap baris. Stok FD berkurang, produk jadi bertambah (1:1, satu nomor transaksi)." testid="bake-form">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <DateField value={f.date} onChange={f.setDate} testid="bake-date" />
          <PicSelect value={f.pic} onChange={f.setPic} testid="bake-pic" />
        </div>
        <Field label="Baris panggang" required>
          <BakeLines lines={f.lines} setLines={f.setLines} products={products} />
        </Field>
        <Field label="Catatan"><Textarea value={f.notes} onChange={(e) => f.setNotes(e.target.value)} data-testid="bake-notes" /></Field>
        <PrimaryBtn className="w-full" onClick={submit} disabled={saving} data-testid="bake-submit">{saving ? "Menyimpan…" : "Panggang & Simpan"}</PrimaryBtn>
      </div>
    </Shell>
  );
}

export { Shell as FormShell, useFormReset };
